import { useState, useRef, useEffect } from 'react';
import { MapPin, CaretDown, Check, Plus, X } from '@phosphor-icons/react';

/**
 * TripSelector - Dropdown to filter events by trip
 */
export default function TripSelector({ 
  trips = [], 
  selectedTripId, 
  onSelectTrip,
  onCreateTrip,
  loading = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTripName, setNewTripName] = useState('');
  const [creating, setCreating] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Find selected trip
  const selectedTrip = trips.find(t => t.id === selectedTripId);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setNewTripName('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus input when showing create form
  useEffect(() => {
    if (showCreateForm && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCreateForm]);

  async function handleCreate() {
    if (!newTripName.trim() || creating) return;
    
    setCreating(true);
    try {
      await onCreateTrip(newTripName.trim());
      setNewTripName('');
      setShowCreateForm(false);
    } finally {
      setCreating(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      handleCreate();
    } else if (e.key === 'Escape') {
      setShowCreateForm(false);
      setNewTripName('');
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors"
        style={{
          backgroundColor: selectedTripId ? 'var(--color-brand-primary-soft)' : 'var(--color-bg-surface)',
          color: selectedTripId ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
          border: `1px solid ${selectedTripId ? 'var(--color-brand-primary)' : 'var(--color-border-subtle)'}`,
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Filter by trip"
      >
        <MapPin size={16} weight={selectedTripId ? 'fill' : 'regular'} />
        <span className="max-w-[120px] truncate">
          {selectedTrip ? selectedTrip.name : 'All Trips'}
        </span>
        <CaretDown 
          size={14} 
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-1 w-56 py-1 rounded-lg shadow-lg z-50"
          style={{
            backgroundColor: 'var(--color-bg-canvas)',
            border: '1px solid var(--color-border-subtle)',
          }}
        >
          {/* All Trips option */}
          <button
            onClick={() => {
              onSelectTrip(null);
              setIsOpen(false);
            }}
            className="w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors hover:bg-[var(--color-bg-surface)]"
            style={{ color: 'var(--color-text-primary)' }}
          >
            <span>All Trips</span>
            {!selectedTripId && (
              <Check size={16} weight="bold" style={{ color: 'var(--color-status-success)' }} />
            )}
          </button>

          {/* Divider */}
          {trips.length > 0 && (
            <div 
              className="my-1 mx-3"
              style={{ borderTop: '1px solid var(--color-border-subtle)' }}
            />
          )}

          {/* Trip list */}
          {loading ? (
            <div className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
              Loading trips...
            </div>
          ) : trips.length === 0 ? (
            <div className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
              No trips yet
            </div>
          ) : (
            <div className="max-h-48 overflow-y-auto">
              {trips.map(trip => (
                <button
                  key={trip.id}
                  onClick={() => {
                    onSelectTrip(trip.id);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors hover:bg-[var(--color-bg-surface)]"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div 
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: trip.color || '#3A5F82' }}
                    />
                    <span className="truncate">{trip.name}</span>
                    {trip.event_count > 0 && (
                      <span 
                        className="text-xs"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        ({trip.event_count})
                      </span>
                    )}
                  </div>
                  {selectedTripId === trip.id && (
                    <Check size={16} weight="bold" style={{ color: 'var(--color-status-success)' }} />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Create new trip */}
          <div 
            className="mt-1 mx-3 pt-2"
            style={{ borderTop: '1px solid var(--color-border-subtle)' }}
          >
            {showCreateForm ? (
              <div className="flex items-center gap-2 pb-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={newTripName}
                  onChange={(e) => setNewTripName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Trip name..."
                  className="flex-1 px-2 py-1 text-sm rounded"
                  style={{
                    backgroundColor: 'var(--color-bg-surface)',
                    border: '1px solid var(--color-border-subtle)',
                    color: 'var(--color-text-primary)',
                  }}
                  disabled={creating}
                />
                <button
                  onClick={handleCreate}
                  disabled={!newTripName.trim() || creating}
                  className="p-1 rounded transition-colors disabled:opacity-50"
                  style={{ color: 'var(--color-status-success)' }}
                >
                  <Check size={18} weight="bold" />
                </button>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewTripName('');
                  }}
                  className="p-1 rounded transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowCreateForm(true)}
                className="w-full flex items-center gap-2 px-2 py-1.5 mb-1 text-sm rounded transition-colors hover:bg-[var(--color-bg-surface)]"
                style={{ color: 'var(--color-brand-primary)' }}
              >
                <Plus size={16} weight="bold" />
                <span>New Trip</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
